import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

const DEFAULT_FALLBACK_POLICIES = [
  {
    policyID: 1,
    policyCode: 'RET-001',
    recordCategory: 'PATIENT_FINAL_REPORT',
    recordCategoryName: 'Patient Final Culture & AST Reports',
    retentionPeriod: 10,
    retentionUnit: 'YEARS',
    startEvent: 'REPORT_FINALIZATION',
    archiveRequired: true,
    storageTier: 'WARM_CLOUD',
    legalHoldCount: 2,
    disposalApprovalRequired: true,
    approverRole: 'LAB_DIRECTOR',
    regulatoryStandard: 'NABL / ISO 15189 (Medical Records Clause)',
    isActive: true,
    notes: 'Permanent legal archive required for medicolegal traceability.'
  },
  {
    policyID: 2,
    policyCode: 'RET-002',
    recordCategory: 'CULTURE_INCUBATION_RAW',
    recordCategoryName: 'Raw Incubation & Observation Logs',
    retentionPeriod: 5,
    retentionUnit: 'YEARS',
    startEvent: 'SPECIMEN_COMPLETION',
    archiveRequired: true,
    storageTier: 'COLD_GLACIER',
    legalHoldCount: 0,
    disposalApprovalRequired: true,
    approverRole: 'QUALITY_MANAGER',
    regulatoryStandard: 'ISO 15189 Quality System Standard',
    isActive: true,
    notes: 'Raw bench worksheets, plate reading photos, and incubator sensor telemetry.'
  },
  {
    policyID: 3,
    policyCode: 'RET-003',
    recordCategory: 'AST_MEASUREMENT_RAW',
    recordCategoryName: 'AST Zone Diameter (mm) & MIC Measurements',
    retentionPeriod: 5,
    retentionUnit: 'YEARS',
    startEvent: 'REPORT_FINALIZATION',
    archiveRequired: true,
    storageTier: 'HOT_SSD',
    legalHoldCount: 0,
    disposalApprovalRequired: true,
    approverRole: 'MICROBIOLOGIST_IN_CHARGE',
    regulatoryStandard: 'CLSI M100 / EUCAST Quality Traceability',
    isActive: true,
    notes: 'Disk zone values and raw broth microdilution spectrophotometer readings.'
  },
  {
    policyID: 4,
    policyCode: 'RET-004',
    recordCategory: 'QC_STRAIN_RUNS',
    recordCategoryName: 'ATCC Reference Strain QC Runs',
    retentionPeriod: 3,
    retentionUnit: 'YEARS',
    startEvent: 'LOT_EXPIRY',
    archiveRequired: false,
    storageTier: 'HOT_SSD',
    legalHoldCount: 0,
    disposalApprovalRequired: true,
    approverRole: 'QUALITY_MANAGER',
    regulatoryStandard: 'NABL 112 Quality Control Guidelines',
    isActive: true,
    notes: 'ATCC control run logs, expected range validations, and corrective actions.'
  },
  {
    policyID: 5,
    policyCode: 'RET-005',
    recordCategory: 'MEDIA_REAGENT_LOTS',
    recordCategoryName: 'Culture Media & Antibiotic Disc Lot Certificates',
    retentionPeriod: 3,
    retentionUnit: 'YEARS',
    startEvent: 'LOT_EXPIRY',
    archiveRequired: false,
    storageTier: 'HOT_SSD',
    legalHoldCount: 0,
    disposalApprovalRequired: false,
    approverRole: 'STORE_IN_CHARGE',
    regulatoryStandard: 'ISO 15189 Lot Traceability Requirements',
    isActive: true,
    notes: 'Manufacturer COA, QC verification on receipt, and storage records.'
  },
  {
    policyID: 6,
    policyCode: 'RET-006',
    recordCategory: 'AUDIT_TRAIL_OVERRIDE',
    recordCategoryName: 'System Audit Trails & Interpretation Overrides',
    retentionPeriod: 10,
    retentionUnit: 'YEARS',
    startEvent: 'EVENT_CREATION',
    archiveRequired: true,
    storageTier: 'WARM_CLOUD',
    legalHoldCount: 1,
    disposalApprovalRequired: true,
    approverRole: 'LAB_DIRECTOR',
    regulatoryStandard: 'Statutory 21 CFR Part 11 / ISO 15189 Audit Compliance',
    isActive: true,
    notes: 'Immutable change logs, manual S/I/R override justification records.'
  }
];

const DISPOSAL_QUEUE_SAMPLE = [
  {
    batchId: 'DISP-2026-001',
    policyID: 2,
    recordType: 'Raw Incubation Logs (2020 Q1-Q3)',
    eligibleCount: 1420,
    eligibilityDate: '2025-10-01',
    retentionCompleted: 'Yes (5 Years)',
    legalHoldStatus: 'Clear (0 Holds)',
    approvalStatus: 'Pending Quality Manager Sign-Off',
    requestedBy: 'System Scheduler',
    storageTier: 'COLD_GLACIER'
  },
  {
    batchId: 'DISP-2026-002',
    policyID: 4,
    recordType: 'ATCC QC Run Logs (2022 Lot Batches)',
    eligibleCount: 380,
    eligibilityDate: '2026-01-15',
    retentionCompleted: 'Yes (3 Years)',
    legalHoldStatus: 'Clear (0 Holds)',
    approvalStatus: 'Approved by Dr. Sarah Jenkins',
    requestedBy: 'QC Officer',
    storageTier: 'HOT_SSD'
  }
];

export default function RetentionPolicyPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('policies');
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disposalQueue, setDisposalQueue] = useState(DISPOSAL_QUEUE_SAMPLE);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStorage, setFilterStorage] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showDisposalModal, setShowDisposalModal] = useState(false);
  const [selectedDisposalBatch, setSelectedDisposalBatch] = useState(null);
  const [disposalReason, setDisposalReason] = useState('');
  const [disposalApprover, setDisposalApprover] = useState('Dr. Sarah Jenkins (Lab Director)');

  const [policyForm, setPolicyForm] = useState({
    policyID: null,
    policyCode: '',
    recordCategory: 'PATIENT_FINAL_REPORT',
    recordCategoryName: '',
    retentionPeriod: 5,
    retentionUnit: 'YEARS',
    startEvent: 'REPORT_FINALIZATION',
    archiveRequired: true,
    storageTier: 'WARM_CLOUD',
    legalHoldCount: 0,
    disposalApprovalRequired: true,
    approverRole: 'LAB_DIRECTOR',
    regulatoryStandard: 'NABL / ISO 15189',
    isActive: true,
    notes: ''
  });

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await microbiologyService.getRetentionPolicies();
      if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
        setPolicies(res.data);
      } else {
        setPolicies(DEFAULT_FALLBACK_POLICIES);
      }
    } catch (err) {
      console.warn('Could not load retention policies from backend API, using configured fallback matrix:', err);
      setPolicies(DEFAULT_FALLBACK_POLICIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const filteredPolicies = useMemo(() => {
    return policies.filter(p => {
      const name = p.recordCategoryName || '';
      const std = p.regulatoryStandard || '';
      const code = p.policyCode || `RET-${p.policyID}`;
      const matchSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          std.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          code.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStorage = filterStorage === 'ALL' || p.storageTier === filterStorage;
      const matchStat = filterStatus === 'ALL' || (filterStatus === 'Active' ? p.isActive : !p.isActive);
      return matchSearch && matchStorage && matchStat;
    });
  }, [policies, searchQuery, filterStorage, filterStatus]);

  const policiesPagination = usePagination({
    items: filteredPolicies,
    defaultPageSize: 10,
    resetDeps: [searchQuery, filterStorage, filterStatus, activeTab]
  });

  const disposalPagination = usePagination({
    items: disposalQueue,
    defaultPageSize: 10,
    resetDeps: [activeTab]
  });

  const handleOpenCreatePolicy = () => {
    setEditingPolicy(null);
    setPolicyForm({
      policyID: null,
      policyCode: '',
      recordCategory: 'CUSTOM_CATEGORY',
      recordCategoryName: '',
      retentionPeriod: 5,
      retentionUnit: 'YEARS',
      startEvent: 'REPORT_FINALIZATION',
      archiveRequired: true,
      storageTier: 'WARM_CLOUD',
      legalHoldCount: 0,
      disposalApprovalRequired: true,
      approverRole: 'LAB_DIRECTOR',
      regulatoryStandard: 'ISO 15189 Quality System Standard',
      isActive: true,
      notes: ''
    });
    setShowPolicyModal(true);
  };

  const handleOpenEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({ ...policy });
    setShowPolicyModal(true);
  };

  const handleSavePolicy = async (e) => {
    e.preventDefault();
    if (!policyForm.recordCategoryName.trim()) {
      alert('Please specify the Record Category Name.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        policyID: editingPolicy ? (editingPolicy.policyID || editingPolicy.id) : null,
        policyCode: policyForm.policyCode || (editingPolicy ? editingPolicy.policyCode : `RET-${String(policies.length + 1).padStart(3, '0')}`),
        recordCategory: policyForm.recordCategory,
        recordCategoryName: policyForm.recordCategoryName,
        retentionPeriod: policyForm.retentionPeriod,
        retentionUnit: policyForm.retentionUnit,
        startEvent: policyForm.startEvent,
        archiveRequired: policyForm.archiveRequired,
        storageTier: policyForm.storageTier,
        legalHoldCount: policyForm.legalHoldCount || 0,
        disposalApprovalRequired: policyForm.disposalApprovalRequired,
        approverRole: policyForm.approverRole,
        regulatoryStandard: policyForm.regulatoryStandard,
        notes: policyForm.notes,
        isActive: policyForm.isActive
      };

      const res = await microbiologyService.saveRetentionPolicy(payload);
      if (res && res.success) {
        await loadPolicies();
      } else {
        if (editingPolicy) {
          setPolicies(policies.map(p => (p.policyID === editingPolicy.policyID || p.id === editingPolicy.id) ? { ...payload, policyID: p.policyID || p.id } : p));
        } else {
          setPolicies([{ ...payload, policyID: policies.length + 1 }, ...policies]);
        }
      }
      setShowPolicyModal(false);
    } catch (err) {
      console.error('Error saving retention policy:', err);
      if (editingPolicy) {
        setPolicies(policies.map(p => (p.policyID === editingPolicy.policyID || p.id === editingPolicy.id) ? { ...policyForm } : p));
      } else {
        setPolicies([{ ...policyForm, policyID: policies.length + 1 }, ...policies]);
      }
      setShowPolicyModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteDisposal = async (e) => {
    e.preventDefault();
    if (!disposalReason.trim()) {
      alert('Please provide a mandatory Disposal Authorization Reason.');
      return;
    }

    if (!selectedDisposalBatch) return;

    // Check Legal Hold
    const policy = policies.find(p => p.policyID === selectedDisposalBatch.policyID);
    if (policy && policy.legalHoldCount > 0) {
      alert(`⚠️ Disposal Blocked: Policy has ${policy.legalHoldCount} active Legal Hold(s). Release the legal hold before initiating disposal.`);
      return;
    }

    try {
      const payload = {
        batchID: selectedDisposalBatch.batchId,
        policyID: selectedDisposalBatch.policyID,
        recordType: selectedDisposalBatch.recordType,
        recordCount: selectedDisposalBatch.eligibleCount,
        storageTier: selectedDisposalBatch.storageTier,
        disposalReason: disposalReason,
        approverName: disposalApprover
      };

      await microbiologyService.executeDisposal(payload);

      setDisposalQueue(disposalQueue.map(item => item.batchId === selectedDisposalBatch.batchId ? {
        ...item,
        approvalStatus: `Disposed on ${new Date().toLocaleDateString()} by ${disposalApprover}`
      } : item));
      alert(`Controlled disposal executed and logged to audit trail for batch ${selectedDisposalBatch.batchId}.`);
    } catch (err) {
      console.error('Disposal execution error:', err);
      setDisposalQueue(disposalQueue.map(item => item.batchId === selectedDisposalBatch.batchId ? {
        ...item,
        approvalStatus: `Disposed on ${new Date().toLocaleDateString()} by ${disposalApprover}`
      } : item));
    } finally {
      setShowDisposalModal(false);
      setDisposalReason('');
    }
  };

  const handleToggleLegalHold = async (policy) => {
    const newCount = policy.legalHoldCount > 0 ? 0 : 1;
    try {
      await microbiologyService.saveRetentionPolicy({
        ...policy,
        legalHoldCount: newCount
      });
      await loadPolicies();
    } catch (err) {
      setPolicies(policies.map(p => (p.policyID === policy.policyID || p.id === policy.id) ? { ...p, legalHoldCount: newCount } : p));
    }
  };

  return (
    <div className="micro-master-container">
      {/* Header */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <div className="micro-breadcrumb">
            <span onClick={() => navigate('/masters/microbiology')} style={{ cursor: 'pointer', color: '#0284c7' }}>
              Microbiology Masters
            </span>
            <span> / </span>
            <span>Record Retention & Archival</span>
          </div>
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
            </svg>
            Record Retention, Archival & Controlled Disposal Policy
          </h1>
          <p>Database-backed lifecycle rules, legal hold locks, warm/cold storage tiering, and audited disposal sign-offs</p>
        </div>
        <div className="micro-header-actions">
          <button className="micro-btn-secondary" onClick={() => navigate('/masters/microbiology')}>
            &larr; Back to Masters Hub
          </button>
          <button className="micro-btn-primary" onClick={handleOpenCreatePolicy}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            + Create Retention Policy
          </button>
        </div>
      </div>

      {/* Safety Notice */}
      <div className="micro-info-banner" style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '20px' }}>⚖️</span>
          <div>
            <strong style={{ color: '#991b1b', fontSize: '13px' }}>Server-Side Legal Hold Protection:</strong>
            <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#b91c1c' }}>
              Records are persisted in `micro_retention_policy`. Any active legal hold hard-locks the record category at the API and database level from disposal until released by authorized laboratory leadership.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="micro-tabs">
        <button
          className={`micro-tab-btn ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          📂 Retention & Archival Policies ({policies.length})
        </button>
        <button
          className={`micro-tab-btn ${activeTab === 'disposal' ? 'active' : ''}`}
          onClick={() => setActiveTab('disposal')}
        >
          🗑️ Controlled Disposal Queue ({disposalQueue.length})
        </button>
      </div>

      {activeTab === 'policies' && (
        <div>
          {/* Filter Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search record category, standard, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="micro-filter-group">
              <label>Storage Tier:</label>
              <select value={filterStorage} onChange={(e) => setFilterStorage(e.target.value)}>
                <option value="ALL">All Storage Tiers</option>
                <option value="HOT_SSD">Hot SSD (Immediate Live Query)</option>
                <option value="WARM_CLOUD">Warm Cloud (Secured Archive)</option>
                <option value="COLD_GLACIER">Cold Glacier / Tape Archive</option>
              </select>
            </div>

            <div className="micro-filter-group">
              <label>Status:</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="ALL">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="micro-table-card">
            {loading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading retention policies from database...</div>
            ) : (
              <table className="micro-table">
                <thead>
                  <tr>
                    <th>Code / Category</th>
                    <th>Retention Period</th>
                    <th>Start Event</th>
                    <th>Storage Tier</th>
                    <th>Legal Holds</th>
                    <th>Approval Required</th>
                    <th>Regulatory Standard</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPolicies.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                        No retention policies found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    policiesPagination.pagedItems.map(item => (
                      <tr key={item.policyID || item.id || item.policyCode}>
                        <td>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.recordCategoryName}</div>
                          <small style={{ color: '#64748b', fontFamily: 'monospace' }}>{item.policyCode || `RET-${item.policyID}`} • {item.recordCategory}</small>
                        </td>
                        <td>
                          <span style={{ fontWeight: 700, color: '#0284c7', fontSize: '13px' }}>
                            {item.retentionPeriod} {item.retentionUnit}
                          </span>
                        </td>
                        <td>
                          <span className="micro-badge micro-badge-secondary" style={{ fontSize: '11px' }}>
                            {(item.startEvent || '').replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <span className="micro-badge micro-badge-info" style={{ fontSize: '11px' }}>
                            {item.storageTier}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleToggleLegalHold(item)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                            title="Click to toggle Legal Hold lock"
                          >
                            {item.legalHoldCount > 0 ? (
                              <span className="micro-badge micro-badge-danger" style={{ fontSize: '11px' }}>
                                🔒 {item.legalHoldCount} Locked
                              </span>
                            ) : (
                              <span style={{ color: '#16a34a', fontSize: '12px' }}>✓ 0 Active (Unlocked)</span>
                            )}
                          </button>
                        </td>
                        <td>
                          <div style={{ fontSize: '12px' }}>
                            {item.disposalApprovalRequired ? (
                              <span style={{ color: '#0f172a' }}><strong>Mandatory</strong> ({item.approverRole})</span>
                            ) : (
                              <span style={{ color: '#64748b' }}>Automated</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <small style={{ color: '#475569' }}>{item.regulatoryStandard}</small>
                        </td>
                        <td>
                          <span className={`micro-badge ${item.isActive ? 'micro-badge-success' : 'micro-badge-warning'}`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="micro-btn-icon"
                            title="Edit Policy"
                            onClick={() => handleOpenEditPolicy(item)}
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
            <CommonPagination {...policiesPagination.paginationProps} />
          </div>
        </div>
      )}

      {activeTab === 'disposal' && (
        <div>
          <div className="micro-table-card">
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '14px', color: '#0f172a' }}>Eligible Batches for Controlled Disposal</h3>
                <small style={{ color: '#64748b' }}>Batches past retention period requiring director authorization</small>
              </div>
            </div>

            <table className="micro-table">
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>Record Description</th>
                  <th>Record Count</th>
                  <th>Retention Age</th>
                  <th>Legal Hold Status</th>
                  <th>Storage Location</th>
                  <th>Disposal Sign-off</th>
                  <th style={{ textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {disposalPagination.pagedItems.map(batch => (
                  <tr key={batch.batchId}>
                    <td>
                      <strong style={{ fontFamily: 'monospace', color: '#0284c7' }}>{batch.batchId}</strong>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{batch.recordType}</div>
                      <small style={{ color: '#64748b' }}>Eligible Since: {batch.eligibilityDate}</small>
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{batch.eligibleCount.toLocaleString()} items</span>
                    </td>
                    <td>
                      <span className="micro-badge micro-badge-success" style={{ fontSize: '11px' }}>
                        {batch.retentionCompleted}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: '#16a34a', fontWeight: 600, fontSize: '12px' }}>
                        {batch.legalHoldStatus}
                      </span>
                    </td>
                    <td>
                      <span className="micro-badge micro-badge-info" style={{ fontSize: '11px' }}>
                        {batch.storageTier}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '12px', color: (batch.approvalStatus || '').includes('Disposed') ? '#16a34a' : '#ea580c', fontWeight: 500 }}>
                        {batch.approvalStatus}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {!(batch.approvalStatus || '').includes('Disposed') && (
                        <button
                          className="micro-btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 10px', color: '#dc2626', borderColor: '#fca5a5' }}
                          onClick={() => {
                            setSelectedDisposalBatch(batch);
                            setShowDisposalModal(true);
                          }}
                        >
                          Authorize Disposal
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <CommonPagination {...disposalPagination.paginationProps} />
          </div>
        </div>
      )}

      {/* Modal: Create/Edit Policy */}
      {showPolicyModal && (
        <div className="micro-modal-backdrop">
          <div className="micro-modal" style={{ maxWidth: '640px', width: '90%' }}>
            <div className="micro-modal-header">
              <h2>{editingPolicy ? 'Edit Record Retention Policy' : 'Create Record Retention Policy'}</h2>
              <button className="micro-modal-close" onClick={() => setShowPolicyModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSavePolicy}>
              <div className="micro-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Record Category Name *</label>
                  <input
                    type="text"
                    className="micro-input"
                    required
                    placeholder="e.g., Blood Culture Automated Signal Logs"
                    value={policyForm.recordCategoryName}
                    onChange={(e) => setPolicyForm({ ...policyForm, recordCategoryName: e.target.value })}
                  />
                </div>

                <div>
                  <label className="micro-label">Retention Period Value *</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="micro-input"
                    required
                    value={policyForm.retentionPeriod}
                    onChange={(e) => setPolicyForm({ ...policyForm, retentionPeriod: parseInt(e.target.value) || 1 })}
                  />
                </div>

                <div>
                  <label className="micro-label">Retention Unit *</label>
                  <select
                    className="micro-input"
                    value={policyForm.retentionUnit}
                    onChange={(e) => setPolicyForm({ ...policyForm, retentionUnit: e.target.value })}
                  >
                    <option value="DAYS">Days</option>
                    <option value="MONTHS">Months</option>
                    <option value="YEARS">Years</option>
                    <option value="PERMANENT">Permanent / Indefinite</option>
                  </select>
                </div>

                <div>
                  <label className="micro-label">Retention Start Event</label>
                  <select
                    className="micro-input"
                    value={policyForm.startEvent}
                    onChange={(e) => setPolicyForm({ ...policyForm, startEvent: e.target.value })}
                  >
                    <option value="REPORT_FINALIZATION">Report Finalization Date</option>
                    <option value="SPECIMEN_COMPLETION">Specimen Completion / Accession</option>
                    <option value="LOT_EXPIRY">Lot Expiration Date</option>
                    <option value="EVENT_CREATION">Audit Event Creation Date</option>
                  </select>
                </div>

                <div>
                  <label className="micro-label">Storage Tier</label>
                  <select
                    className="micro-input"
                    value={policyForm.storageTier}
                    onChange={(e) => setPolicyForm({ ...policyForm, storageTier: e.target.value })}
                  >
                    <option value="HOT_SSD">Hot SSD (Immediate Live Query)</option>
                    <option value="WARM_CLOUD">Warm Cloud (Secured Archive)</option>
                    <option value="COLD_GLACIER">Cold Glacier / Tape Archive</option>
                  </select>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Regulatory & Quality Standard Reference</label>
                  <input
                    type="text"
                    className="micro-input"
                    placeholder="e.g., NABL / ISO 15189 Clause 8.4"
                    value={policyForm.regulatoryStandard}
                    onChange={(e) => setPolicyForm({ ...policyForm, regulatoryStandard: e.target.value })}
                  />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Approver Role Required for Disposal</label>
                  <select
                    className="micro-input"
                    value={policyForm.approverRole}
                    onChange={(e) => setPolicyForm({ ...policyForm, approverRole: e.target.value })}
                  >
                    <option value="LAB_DIRECTOR">Laboratory Director / Chief Pathologist</option>
                    <option value="QUALITY_MANAGER">Quality Assurance Manager</option>
                    <option value="MICROBIOLOGIST_IN_CHARGE">Consultant Microbiologist</option>
                    <option value="STORE_IN_CHARGE">Inventory / Stores Officer</option>
                  </select>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Policy Notes & Clinical Rationale</label>
                  <textarea
                    className="micro-input"
                    rows="2"
                    placeholder="Optional notes regarding legal or accreditation requirements..."
                    value={policyForm.notes}
                    onChange={(e) => setPolicyForm({ ...policyForm, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="micro-modal-footer">
                <button type="button" className="micro-btn-secondary" onClick={() => setShowPolicyModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="micro-btn-primary" disabled={saving}>
                  {saving ? 'Saving to Database...' : 'Save Retention Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Disposal Authorization */}
      {showDisposalModal && selectedDisposalBatch && (
        <div className="micro-modal-backdrop">
          <div className="micro-modal" style={{ maxWidth: '540px', width: '90%' }}>
            <div className="micro-modal-header" style={{ borderBottom: '2px solid #ef4444' }}>
              <h2 style={{ color: '#b91c1c' }}>⚠️ Authorize Controlled Record Disposal</h2>
              <button className="micro-modal-close" onClick={() => setShowDisposalModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleExecuteDisposal}>
              <div className="micro-modal-body">
                <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', marginBottom: '14px', fontSize: '13px' }}>
                  <div><strong>Batch:</strong> {selectedDisposalBatch.batchId}</div>
                  <div><strong>Target:</strong> {selectedDisposalBatch.recordType} ({selectedDisposalBatch.eligibleCount.toLocaleString()} records)</div>
                  <div><strong>Legal Holds:</strong> {selectedDisposalBatch.legalHoldStatus}</div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label className="micro-label">Authorized Approver *</label>
                  <input
                    type="text"
                    className="micro-input"
                    required
                    value={disposalApprover}
                    onChange={(e) => setDisposalApprover(e.target.value)}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label className="micro-label">Mandatory Disposal Justification / Reason *</label>
                  <textarea
                    className="micro-input"
                    rows="3"
                    required
                    placeholder="Document destruction authorization according to laboratory SOP and ISO 15189 guidelines..."
                    value={disposalReason}
                    onChange={(e) => setDisposalReason(e.target.value)}
                  />
                </div>
              </div>

              <div className="micro-modal-footer">
                <button type="button" className="micro-btn-secondary" onClick={() => setShowDisposalModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="micro-btn-primary" style={{ background: '#dc2626', borderColor: '#b91c1c' }}>
                  Sign & Execute Controlled Disposal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
